// Pure, seed-deterministic world generation. No Pixi, no DOM. (TECHNICAL_DESIGN.md §2, §6.)
import { CHUNK, CHUNK_TILES, NODE_HP } from './const';
import { fbm2, hash3i, toUnit } from './rng';
import type { Biome, Chunk, NodeKind, TileId, WorldNode } from './types';

const ELEV_SCALE = 22;
const MOIST_SCALE = 30;

/** Classify a world tile into a biome from elevation + moisture noise. */
export function biomeAt(seed: number, tx: number, ty: number): Biome {
  const e = fbm2(seed, 1, tx, ty, ELEV_SCALE);
  const m = fbm2(seed, 2, tx, ty, MOIST_SCALE);
  if (e < 0.3) return 'water';
  if (e < 0.36) return 'beach';
  if (e > 0.72) return 'highlands';
  return m > 0.5 ? 'forest' : 'meadow';
}

export function tileOfBiome(b: Biome): TileId {
  switch (b) {
    case 'water': return 0;
    case 'beach': return 1;
    case 'meadow': return 2;
    case 'forest': return 3;
    case 'highlands': return 4;
  }
}

export function isSolidTile(t: TileId): boolean {
  return t === 0; // water blocks movement
}

/** Which node kind (if any) a given biome may spawn, given a roll in [0,1]. */
function nodeForBiome(b: Biome, roll: number): NodeKind | null {
  // Densities tuned so even an all-forest 3×3 chunk ring stays under the ~300 on-screen
  // object budget (TECHNICAL_DESIGN.md §5): forest ≈ 5.5% of tiles → ~32 nodes/chunk.
  switch (b) {
    case 'meadow':
      if (roll < 0.025) return 'tree';
      if (roll < 0.045) return 'bush';
      return null;
    case 'forest':
      if (roll < 0.04) return 'tree';
      if (roll < 0.055) return 'bush';
      return null;
    case 'highlands':
      if (roll < 0.025) return 'stone';
      if (roll < 0.04) return 'iron';
      if (roll < 0.05) return 'gold';
      return null;
    default:
      return null;
  }
}

function nodeMaxHp(kind: NodeKind): number {
  switch (kind) {
    case 'tree': return NODE_HP.tree;
    case 'bush': return NODE_HP.bush;
    case 'stone': return NODE_HP.stone;
    case 'iron': return NODE_HP.iron;
    case 'gold': return NODE_HP.gold;
    default: return 1;
  }
}

/**
 * Generate a chunk purely from (seed, cx, cy). RESOURCE nodes are emitted first in a STABLE
 * row-major order (so their nodeKey indices never shift — the save-compat invariant, §6), and
 * entrances are appended AFTER in a second pass (new kinds must not renumber existing ones).
 */
export function genChunk(seed: number, cx: number, cy: number): Chunk {
  const tiles = new Uint8Array(CHUNK_TILES);
  const nodes: WorldNode[] = [];
  let nodeIndex = 0;

  // pass 1 — resource nodes (indices 0..k, byte-identical across versions)
  for (let ly = 0; ly < CHUNK; ly++) {
    for (let lx = 0; lx < CHUNK; lx++) {
      const tx = cx * CHUNK + lx;
      const ty = cy * CHUNK + ly;
      const b = biomeAt(seed, tx, ty);
      tiles[ly * CHUNK + lx] = tileOfBiome(b);

      if (b !== 'water') {
        const roll = toUnit(hash3i(seed, tx, ty, 0x51));
        const kind = nodeForBiome(b, roll);
        if (kind) {
          nodes.push({ key: `${cx},${cy}:${nodeIndex}`, kind, tx, ty, maxHp: nodeMaxHp(kind) });
          nodeIndex++;
        }
      }
    }
  }

  // pass 2 — dungeon entrances (appended after resources). At most one per chunk, on land,
  // deterministically placed. One chunk near spawn is guaranteed to hold an entrance.
  const ent = entranceForChunk(seed, cx, cy);
  if (ent) {
    nodes.push({ key: `${cx},${cy}:${nodeIndex}`, kind: 'entrance', tx: ent.tx, ty: ent.ty, maxHp: 1 });
    nodeIndex++;
  }

  // pass 3 — the three grove totems, in the spawn chunk only (appended last, order-stable §6)
  const spawn = findSpawn(seed);
  if (cx === Math.floor(spawn.tx / CHUNK) && cy === Math.floor(spawn.ty / CHUNK)) {
    for (let i = 0; i < 3; i++) {
      const tx = spawn.tx + (i - 1) * 2;
      const ty = spawn.ty - 3;
      if (biomeAt(seed, tx, ty) !== 'water') {
        nodes.push({ key: `${cx},${cy}:${nodeIndex}`, kind: 'totem', tx, ty, maxHp: 1 });
        nodeIndex++;
      }
    }
  }
  return { cx, cy, tiles, nodes };
}

/** Deterministic entrance for a chunk, or null. Sparse, land-only; guaranteed near spawn. */
function entranceForChunk(seed: number, cx: number, cy: number): { tx: number; ty: number } | null {
  const spawn = findSpawn(seed);
  const spawnCx = Math.floor(spawn.tx / CHUNK);
  const spawnCy = Math.floor(spawn.ty / CHUNK);
  const isSpawnEntranceChunk = cx === spawnCx + 1 && cy === spawnCy; // one chunk east of spawn
  const roll = toUnit(hash3i(seed, cx, cy, 0xe7));
  if (!isSpawnEntranceChunk && roll > 0.12) return null; // ~12% of other chunks
  // pick a land tile near chunk centre
  for (let r = 0; r < CHUNK; r++) {
    const lx = (Math.floor(toUnit(hash3i(seed, cx, cy, 0xa1 + r)) * CHUNK));
    const ly = (Math.floor(toUnit(hash3i(seed, cx, cy, 0xb2 + r)) * CHUNK));
    const tx = cx * CHUNK + lx;
    const ty = cy * CHUNK + ly;
    if (biomeAt(seed, tx, ty) !== 'water') return { tx, ty };
  }
  return null;
}

/** Is a world point within corrupted ground (near any dungeon entrance)? Pure. */
export function corruptionAt(seed: number, wx: number, wy: number, radius: number): boolean {
  const cx = Math.floor(wx / (CHUNK * 32));
  const cy = Math.floor(wy / (CHUNK * 32));
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const ent = entranceForChunk(seed, cx + dx, cy + dy);
      if (ent) {
        const ex = ent.tx * 32 + 16;
        const ey = ent.ty * 32 + 16;
        if (Math.hypot(ex - wx, ey - wy) <= radius) return true;
      }
    }
  }
  return false;
}

/** Find a guaranteed-land spawn tile near world origin for a fresh world. */
export function findSpawn(seed: number): { tx: number; ty: number } {
  for (let r = 0; r < 200; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        const b = biomeAt(seed, dx, dy);
        if (b === 'meadow' || b === 'forest') return { tx: dx, ty: dy };
      }
    }
  }
  return { tx: 0, ty: 0 };
}
